import type { MetaHTMLAttributes } from 'react'
import { isAxiosError } from 'axios'
import { redirect, useParams } from 'react-router'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import { DocumentMeta } from '../components/DocumentMeta'
import { addLegacyProductProperties, track } from '../utils/tracking'
import { addProduct, setAction } from '../utils/googleAnalytics'
import { createMultiLanguageLinks } from './utils/createMultiLanguageLinks'
import { extractJrestIdFromHref as extractGUID } from '../utils/extractId'
import { generateProductUrl, withQueryAndScope } from '../urlGeneratorsPlain'
import { getSocialSharingEnabled, loadCart, loadProduct } from '../store/actions'
import Theme from '../components/Theme'
import useEffectExceptMount from '../utils/hooks/useEffectExceptMount'
import usePrevious from '../utils/hooks/usePrevious'

export default function Product() {
  const params = useParams()
  const productId = params.productId

  const product = useSelector<State, ImmutableMap>((state) => state.get('products').get(productId))
  const viewError = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'error']))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))
  const cart = useSelector<State, ImmutableMap>((state) => state.get('cart'))

  // Tracking
  const trackProductDetails = useCallback(
    (product: Frontend.Product, cart: Frontend.Cart) => {
      track('product:view', {
        product: addLegacyProductProperties(product, shop.get('beyond')),
        cart,
      })
      addProduct(product, ['id', 'name', 'variant', 'brand', 'price', 'currency'])
      setAction('detail')
    },
    [shop],
  )
  const previousProduct = usePrevious(product)
  useEffectExceptMount(() => {
    if (product && !previousProduct?.equals(product)) {
      trackProductDetails(product.toJS(), cart.toJS())
    }
  }, [previousProduct, product, trackProductDetails, cart])
  useEffect(() => {
    if (product) trackProductDetails(product.toJS(), cart.toJS())
    // It should only trigger on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (viewError || !product) {
    return (
      <>
        <DocumentMeta />
        <Theme withLayout error={viewError} currentView="product" />
      </>
    )
  }

  const productTitle = product.get('title') || product.get('name')
  const documentTitle = product.get('title')
    ? productTitle
    : [productTitle, shop.get('title')].filter(Boolean).join(' - ')

  // In multi language shops, the default locale will not get a localized path, but any other language requires it.
  const pathLocale = shop.get('defaultLocale') === shop.get('locale') ? '' : `/${shop.get('locale').substr(0, 2)}`
  const baseHref = shop.getIn(['locationBase', 'origin']) + pathLocale

  const meta: MetaHTMLAttributes<HTMLMetaElement>[] = []
  meta.push(
    { property: 'og:type', content: 'product' },
    { property: 'og:title', content: productTitle },
    { property: 'og:url', content: baseHref + product.get('href') },
  )

  if (product.get('image')) {
    let productImageUrl = product.getIn(['image', 'url'])

    if (!productImageUrl.startsWith('http')) productImageUrl = shop.getIn(['locationBase', 'origin']) + productImageUrl

    meta.push(
      { property: 'og:image', content: productImageUrl },
      { property: 'og:image:width', content: product.getIn(['image', 'width']) },
      { property: 'og:image:height', content: product.getIn(['image', 'height']) },
    )
  }
  if (product.get('price')) {
    meta.push(
      { property: 'og:price:amount', content: product.getIn(['price', 'amount']) },
      { property: 'og:price:currency', content: product.getIn(['price', 'currency']) },
    )
  }
  if (product.get('metaDescription')) {
    meta.push(
      { name: 'description', content: product.get('metaDescription') },
      { property: 'og:description', content: product.get('metaDescription') },
    )
  }

  return (
    <>
      <DocumentMeta
        title={documentTitle}
        meta={meta}
        links={
          product.get('isVariationProduct')
            ? [
                {
                  rel: 'canonical',
                  href: `${baseHref}/p/${product.getIn(['variationMaster', 'slug'])}`,
                },
              ]
            : createMultiLanguageLinks(
                'product',
                product.get('slugs', Immutable.List()).toJS(),
                shop,
                product.get('productId'),
              )
        }
      />
      <Theme withLayout currentView="product">
        {(renderView, props) =>
          renderView('Product', {
            ...props,
            product: product.toJS(),
          })
        }
      </Theme>
    </>
  )
}

function getLoadProductAction(state: State, productId: string, options?: ActionOptions) {
  const sellingCountryId = state.getIn(['shop', 'sellingCountryId']) || null

  // look for the GUID to the requested slug in the param or somewhere in the state in order to save a db roundtrip
  let guid: string | null = extractGUID(productId)

  const categoryProducts = state
    .get('categoryProductData', Immutable.Map())
    .valueSeq()
    .map((a) => a.products)
    .reduce((a, b) => a.concat(b), [])
  const searchProducts = state
    .get('searchData', Immutable.Map())
    .valueSeq()
    .map((a) => a.products)
    .reduce((a, b) => a.concat(b), [])
  const productWithSlug = [...categoryProducts, ...searchProducts]
    .filter(Boolean)
    .find((product) => product.slug === productId)

  if (productWithSlug) guid = productWithSlug.productId

  return loadProduct(
    {
      guid: guid || productId,
      slug: guid && productId,
    },
    sellingCountryId,
    options,
  )
}

Product.validationLoader = ((_api, store) =>
  async ({ request, params }) => {
    // Redirect /p/LONG-GUID to /p/pretty-slug when we don't yet have the slug in the database.

    const isForwardNavigationOnClient = typeof window !== 'undefined' && window.getNavigationType?.() === 'PUSH'
    const state = store.getState()

    // On client side navigation, the product might already be in the state.
    let product: Core.Product | undefined = state.getIn(['products', params.productId])?.toJS()

    // If the product is not already in the state (e.g. on server side rendering or when navigating to a
    // product that is not yet in the state), load it. Always load it when navigating forward on the client,
    // as product data might have changed.
    if (!product || isForwardNavigationOnClient) {
      // Load the product (which triggers slugification) to get the current slug.
      // Note: We dispatch it to the store as a side effect, so that the product
      // is available in the store for rendering the Product component.
      try {
        const dispatch = store.dispatch as GlobalDispatch
        const data = await dispatch(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          getLoadProductAction(state, params.productId!, {
            showErrorNotification: false,
          }),
        )
        product = data?.product
      } catch (exception) {
        // If the product is not found, we ignore it so that the standard 404 page is rendered
        // (via Product.loader below). Any other error is re-thrown.
        const isProductNotFound = isAxiosError(exception) && exception.response?.status === 404
        if (!isProductNotFound) {
          throw exception
        }
      }
    }

    // If the found product has a different slug or the page was requested with
    // a GUID in the URL, redirect to the correct product URL.
    if (product && product.slug !== params.productId) {
      return redirect(withQueryAndScope(generateProductUrl(product.slug), request.url), {
        status: 301,
        headers: {
          // disable browser redirect-cache, since redirect might get re-used as slug later
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
      })
    }

    return null
  }) satisfies View.LoaderFn

Product.storeUpdate = ((_props, state) => {
  // Note: We don't load the product here, as it is already loaded in
  // `Product.validationLoader` (see above).

  const updates: ApiAction<any>[] = []

  if (!state.hasIn(['shop', 'socialSharingEnabled'])) updates.push(getSocialSharingEnabled())
  if (!state.get('cart')) updates.push(loadCart())

  return updates
}) satisfies View.StoreUpdateFn<'productId'>

Product.loader = ((_api, store) =>
  ({ params }) => {
    const state = store.getState()
    const product = state.getIn(['products', params.productId])
    if (!product) {
      // If the product was not loaded to the store (via validationLoader above),
      // then no product was found for the request so we show a 404 page.
      throw new Response('Not Found', { status: 404 })
    }
  }) satisfies View.LoaderFn

Product.loadableSsrChunks = ['Product']
Product.contentCreationDisabled = () => true
